import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { Store, Briefcase, CalendarDays, FolderKanban, Building2, Luggage, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { regionLabel } from '../lib/regionNames';
import { CLASSIFIED_CATEGORIES } from '../data/classifiedsCategories';
import ActivityFeed from './ActivityFeed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The catalogue categories shown as chips below the map. Order mirrors the
// user's request: Veikali, Pakalpojumi, Pasākumi, Projekti, Uzņēmumi, Sludinājumi.
// Colours are distinct from the geography layers (Pilsētas=orange, Mikrorajoni=
// blue, Ciemi=green) and are admin-editable (fetched from /api/map/colors).
// Sludinājumi (private classifieds, C2C) uses the brand teal and a 🧳 suitcase icon.
const CAT_COLOR_DEFAULTS = { shop: '#E11D48', service: '#7C3AED', event: '#DB2777', project: '#0891B2', business: '#CA8A04', classified: '#2F7E79' };
const CATS = [
  { key: 'shop', lv: 'Veikali', en: 'Shops', ru: 'Магазины', Icon: Store },
  { key: 'service', lv: 'Pakalpojumi', en: 'Services', ru: 'Услуги', Icon: Briefcase },
  { key: 'event', lv: 'Pasākumi', en: 'Events', ru: 'События', Icon: CalendarDays },
  { key: 'project', lv: 'Projekti', en: 'Projects', ru: 'Проекты', Icon: FolderKanban },
  { key: 'business', lv: 'Uzņēmumi', en: 'Businesses', ru: 'Компании', Icon: Building2 },
  { key: 'classified', lv: 'Sludinājumi', en: 'Classifieds', ru: 'Объявления', Icon: Luggage },
];
const META = Object.fromEntries(CATS.map((c) => [c.key, c]));

const inBounds = (it, b) => b && it.lng >= b[0] && it.lng <= b[2] && it.lat >= b[1] && it.lat <= b[3];

const isPagePin = (f) => { const t = (f.properties || {}).template; return !!(t && t !== 'null' && t !== ''); };
const mapBizFeature = (f, cat) => {
  const p = f.properties || {};
  const c = f.geometry?.coordinates || [];
  const page = isPagePin(f);
  return {
    id: p.id, category: cat, name: p.name,
    subtitle: p.location_name || p.region || '', thumbnail: p.logo || null,
    lng: c[0], lat: c[1],
    link: page ? (p.page_id ? `/uznemums/${p.id}/${p.page_id}` : `/uznemums/${p.id}`) : `/shop/${p.id}`,
  };
};
const mapListingFeature = (f, cat) => {
  const p = f.properties || {};
  const c = f.geometry?.coordinates || [];
  return {
    id: p.id, category: cat, name: p.name,
    subtitle: p.location_name || p.region || '', thumbnail: p.image || null,
    price: p.price, priceType: p.price_type, lng: c[0], lat: c[1],
    isOnline: !!p.is_online, link: `/listing/${p.id}`,
  };
};

const MapCategoryExplorer = forwardRef(function MapCategoryExplorer({ apiRef, mapWrapRef }, ref) {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);

  const [colors, setColors] = useState(CAT_COLOR_DEFAULTS);
  const col = useCallback((key) => colors[key] || CAT_COLOR_DEFAULTS[key] || '#121212', [colors]);
  useEffect(() => {
    axios.get(`${API}/map/colors`).then(({ data }) => { if (data?.colors) setColors((c) => ({ ...c, ...data.colors })); }).catch(() => {});
  }, []);

  const [selected, setSelected] = useState(null);
  const [visibleOnly, setVisibleOnly] = useState(false);
  const [counts, setCounts] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);   // contextual "Filtrs" for the active category
  const [filters, setFilters] = useState({});            // per-category filters: {min,max,sub,paid}
  const setF = (key, patch) => setFilters((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));
  const cacheRef = useRef({});                    // category -> items[]
  const listingsRef = useRef(null);               // raw /map/companies once
  const companiesPromiseRef = useRef(null);       // de-dupe concurrent fetches
  const businessesRef = useRef(null);             // raw /map/businesses once
  const businessesPromiseRef = useRef(null);      // de-dupe concurrent fetches
  const feedRef = useRef(null);                   // activity feed (scroll target on chip select)

  // --- Fetch raw feature collections once (cached + de-duped) ---
  const loadBusinesses = useCallback(async () => {
    if (!businessesRef.current) {
      if (!businessesPromiseRef.current) {
        businessesPromiseRef.current = axios
          .get(`${API}/map/businesses`, { params: { limit: 3000 } })
          .then(({ data }) => { businessesRef.current = data.features || []; return businessesRef.current; });
      }
      await businessesPromiseRef.current;
    }
    return businessesRef.current;
  }, []);
  const loadCompanies = useCallback(async () => {
    if (!listingsRef.current) {
      if (!companiesPromiseRef.current) {
        companiesPromiseRef.current = axios
          .get(`${API}/map/companies`, { params: { limit: 3000 } })
          .then(({ data }) => { listingsRef.current = data.features || []; return listingsRef.current; });
      }
      await companiesPromiseRef.current;
    }
    return listingsRef.current;
  }, []);

  // --- Fetch + normalise a category's items (cached) ---
  const loadCategory = useCallback(async (key) => {
    if (cacheRef.current[key]) return cacheRef.current[key];
    let out = [];
    if (key === 'business') {
      // Uzņēmumi = business PAGES only (pins WITH a template).
      const feats = await loadBusinesses();
      out = feats.filter(isPagePin).map((f) => mapBizFeature(f, 'business'));
    } else if (key === 'shop') {
      // Veikali = shop ENTITIES only (one pin per store, pins WITHOUT a
      // template). Individual product listings are NOT pinned — a shop shows
      // once on the map, and its products are browsable by opening the store.
      const biz = await loadBusinesses();
      out = biz.filter((f) => !isPagePin(f)).map((f) => mapBizFeature(f, 'shop'));
    } else if (key === 'event') {
      const { data } = await axios.get(`${API}/map/events`);
      out = (data.features || []).map((f) => {
        const p = f.properties || {};
        const c = f.geometry?.coordinates || [];
        return {
          id: p.id, category: 'event', name: p.name,
          subtitle: p.location_name || p.region || '', thumbnail: p.image || null,
          price: p.is_paid ? p.price : null, isPaid: !!p.is_paid, lng: c[0], lat: c[1], link: `/events/${p.id}`,
        };
      });
    } else if (key === 'classified') {
      // Sludinājumi = private C2C classifieds with coordinates (🧳 pins).
      const { data } = await axios.get(`${API}/classifieds/geo`, { params: { limit: 1000 } });
      out = (data || []).map((d) => ({
        id: d.id, category: 'classified', clsCat: d.category, name: d.title,
        subtitle: regionLabel(d.region) || '', thumbnail: d.image || null,
        price: d.price, priceType: d.price_type, lng: d.lng, lat: d.lat,
        emoji: d.emoji, slug: d.slug,
        link: `/sludinajums/${d.slug || d.id}`,
      }));
    } else {
      // service / project → catalogue product listings with resolved coords.
      const comp = await loadCompanies();
      out = comp.filter((f) => (f.properties || {}).category === key).map((f) => mapListingFeature(f, key));
    }
    cacheRef.current[key] = out;
    return out;
  }, [loadBusinesses, loadCompanies]);

  const applyToMap = useCallback((all, color, fit, key) => {
    const api = apiRef?.current;
    if (!api) return;
    const bounds = visibleOnly ? api.getBounds?.() : null;
    const geo = all.filter((it) => it.lng != null && it.lat != null);
    const pins = bounds ? geo.filter((it) => inBounds(it, bounds)) : geo;
    // Classifieds render as the dedicated 🧳 clustered markers; every other
    // category uses the generic (now clustered) explore circles.
    if (key === 'classified') {
      api.clearExplore?.();
      api.setClassifieds?.(pins);
    } else {
      api.setClassifieds?.(null);
      api.showExplore?.(pins, color);
    }
    if (fit && !visibleOnly && pins.length) api.fitExplore?.(pins);
  }, [apiRef, visibleOnly]);

  // Per-category map filters, applied client-side to the pins + results list:
  // price (service/project/classified), free/paid (events), category (classifieds).
  const applyCatFilter = useCallback((key, all) => {
    const f = filters[key] || {};
    let res = all;
    if (f.min || f.max) {
      const mn = f.min ? Number(f.min) : null;
      const mx = f.max ? Number(f.max) : null;
      res = res.filter((it) => {
        const pr = (typeof it.price === 'number') ? it.price : (it.price != null && it.price !== '' ? Number(it.price) : null);
        if (pr == null || Number.isNaN(pr)) return false;
        if (mn != null && pr < mn) return false;
        if (mx != null && pr > mx) return false;
        return true;
      });
    }
    if (key === 'event' && f.paid) {
      res = res.filter((it) => (f.paid === 'free' ? !it.isPaid : !!it.isPaid));
    }
    if (key === 'classified' && f.sub) {
      res = res.filter((it) => it.clsCat === f.sub);
    }
    return res;
  }, [filters]);

  // Live per-category counts for the chips (respects the visible-area toggle).
  const recomputeCounts = useCallback(() => {
    const b = visibleOnly ? apiRef?.current?.getBounds?.() : null;
    const next = {};
    CATS.forEach((c) => {
      const all = cacheRef.current[c.key] || [];
      if (b) {
        const geo = all.filter((it) => it.lng != null && inBounds(it, b)).length;
        const online = all.filter((it) => it.isOnline).length;
        next[c.key] = geo + online;
      } else {
        next[c.key] = all.length;
      }
    });
    setCounts(next);
  }, [apiRef, visibleOnly]);

  // Preload every category once so each chip can show its count immediately.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await Promise.all(CATS.map((c) => loadCategory(c.key))); } catch (_) { /* ignore */ }
      if (!cancelled) recomputeCounts();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute counts when the visible-area mode changes or the map moves.
  useEffect(() => {
    recomputeCounts();
    const map = apiRef?.current?.getMap?.();
    if (!map || !visibleOnly) return undefined;
    const onMove = () => recomputeCounts();
    map.on('moveend', onMove);
    return () => map.off('moveend', onMove);
  }, [visibleOnly, recomputeCounts, apiRef]);

  const handleSelect = useCallback(async (key, opts = {}) => {
    const { force = false, fit = true } = opts;
    if (!force && selected === key) {
      setSelected(null);
      apiRef?.current?.clearExplore?.();
      apiRef?.current?.setClassifieds?.(null);
      return;
    }
    setSelected(key);
    // Selecting a category centres the view on the activity feed / list (not the
    // map top), so the filtered results are immediately visible.
    (feedRef.current || mapWrapRef?.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    try {
      const all = await loadCategory(key);
      applyToMap(applyCatFilter(key, all), col(key), fit, key);
    } catch (_) {
      /* ignore */
    }
  }, [selected, apiRef, mapWrapRef, loadCategory, applyToMap, applyCatFilter, col]);

  // Let the map's region markers drive the explorer: opening a region auto-
  // enables the "only in visible map area" mode (so the chips + list reflect
  // just the region you zoomed into); a satellite also focuses its category.
  useImperativeHandle(ref, () => ({
    selectCategory: (key) => { if (META[key]) handleSelect(key, { force: true, fit: false }); },
    enterRegion: () => setVisibleOnly(true),
  }), [handleSelect]);

  // Re-filter to the visible map area as the user pans/zooms (only when the
  // "visible area" mode is on and a category is active).
  useEffect(() => {
    const api = apiRef?.current;
    const map = api?.getMap?.();
    if (!map || !selected) return undefined;
    // Re-apply immediately when the toggle, selection or filters change.
    const all = applyCatFilter(selected, cacheRef.current[selected] || []);
    applyToMap(all, col(selected), false, selected);
    if (!visibleOnly) return undefined;
    const onMove = () => {
      const cur = applyCatFilter(selected, cacheRef.current[selected] || []);
      applyToMap(cur, col(selected), false, selected);
    };
    map.on('moveend', onMove);
    return () => map.off('moveend', onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, visibleOnly, filters]);

  // Specific filters for ONE category (shown contextually under the active
  // pill — no duplicate category list). Mirrors the previous per-category
  // settings: price (service/project/classified), free/paid (event),
  // subcategory (classified).
  const renderSpecificFilters = (key) => {
    const f = filters[key] || {};
    const PriceRange = (
      <div>
        <p className="text-xs font-semibold text-[#5C5C5C] mb-1.5">{tr('Cena', 'Price', 'Цена')}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" min="0" inputMode="decimal" value={f.min || ''} onChange={(e) => setF(key, { min: e.target.value })}
            placeholder={tr('no €', 'from €', 'от €')} className="w-24 h-9 px-3 rounded-full border border-[#E5E4E0] text-sm focus:outline-none focus:border-[#2F7E79]" data-testid={`filter-price-min-${key}`} />
          <span className="text-[#8A8A8A]">–</span>
          <input type="number" min="0" inputMode="decimal" value={f.max || ''} onChange={(e) => setF(key, { max: e.target.value })}
            placeholder={tr('līdz €', 'to €', 'до €')} className="w-24 h-9 px-3 rounded-full border border-[#E5E4E0] text-sm focus:outline-none focus:border-[#2F7E79]" data-testid={`filter-price-max-${key}`} />
          {(f.min || f.max) && <button type="button" onClick={() => setF(key, { min: '', max: '' })} className="text-xs text-[#5C5C5C] hover:text-[#121212]" data-testid={`filter-price-clear-${key}`}>{tr('Notīrīt', 'Clear', 'Сброс')}</button>}
        </div>
      </div>
    );
    if (key === 'classified') {
      return (
        <div className="space-y-3">
          {PriceRange}
          <div>
            <p className="text-xs font-semibold text-[#5C5C5C] mb-1.5">{tr('Kategorija', 'Category', 'Категория')}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-cls-categories">
              <button type="button" onClick={() => setF('classified', { sub: null })} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${!f.sub ? 'bg-[#2F7E79] text-white border-[#2F7E79]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0]'}`} data-testid="filter-cls-cat-all">{tr('Visas', 'All', 'Все')}</button>
              {CLASSIFIED_CATEGORIES.map((cat) => (
                <button key={cat.id} type="button" onClick={() => setF('classified', { sub: f.sub === cat.id ? null : cat.id })}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${f.sub === cat.id ? 'bg-[#2F7E79] text-white border-[#2F7E79]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0]'}`} data-testid={`filter-cls-cat-${cat.id}`}>
                  {cat.emoji} {tr(cat.lv, cat.en, cat.ru)}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (key === 'service' || key === 'project') return PriceRange;
    if (key === 'event') {
      return (
        <div data-testid="filter-event-type">
          <p className="text-xs font-semibold text-[#5C5C5C] mb-1.5">{tr('Veids', 'Type', 'Тип')}</p>
          <div className="flex gap-1.5">
            {[['', tr('Visi', 'All', 'Все')], ['free', tr('Bezmaksas', 'Free', 'Бесплатно')], ['paid', tr('Maksas', 'Paid', 'Платно')]].map(([val, lbl]) => (
              <button key={val || 'all'} type="button" onClick={() => setF('event', { paid: val })}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${(f.paid || '') === val ? 'bg-[#8B5CF6] text-white border-[#8B5CF6]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0]'}`} data-testid={`filter-event-${val || 'all'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
      );
    }
    // shop / business: subcategory/industry filters need backend taxonomy — next step.
    return <p className="text-xs text-[#9CA3AF]" data-testid={`filter-more-soon-${key}`}>{tr('Papildu filtri drīzumā.', 'More filters coming soon.', 'Дополнительные фильтры скоро.')}</p>;
  };

  const activeFilterCount = (() => {
    if (!selected) return 0;
    const f = filters[selected] || {};
    let n = 0;
    if (f.min || f.max) n += 1;
    if (selected === 'event' && f.paid) n += 1;
    if (selected === 'classified' && f.sub) n += 1;
    if (visibleOnly) n += 1;
    return n;
  })();

  return (
    <section className="mt-4" data-testid="map-category-explorer">
      {/* Category chips — wrap to multiple rows on mobile so all are visible (no horizontal scroll) */}
      <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="explorer-chips">
        {CATS.map((c) => {
          const Icon = c.Icon;
          const on = selected === c.key;
          return (
            <button key={c.key} type="button" onClick={() => handleSelect(c.key)}
              className={`flex items-center gap-2 shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${on ? 'text-white border-transparent shadow-md ring-2 ring-offset-2 ring-offset-white scale-[1.03]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'}`}
              style={on ? { backgroundColor: col(c.key), '--tw-ring-color': col(c.key) } : undefined}
              data-testid={`explorer-chip-${c.key}`} aria-pressed={on}>
              <Icon className="w-4 h-4" />
              {tr(c.lv, c.en, c.ru)}
              {counts[c.key] != null && (
                <span className={`ml-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${on ? 'bg-white/25 text-white' : ''}`}
                  style={on ? undefined : (counts[c.key] > 0
                    ? { color: col(c.key), backgroundColor: `${col(c.key)}1A` }
                    : { color: '#9CA3AF', backgroundColor: '#F3F2EE' })}
                  data-testid={`explorer-count-${c.key}`}>
                  {counts[c.key]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Contextual "Filtrs" — only for the ACTIVE category (no duplicate
          category list). The top pills are the single category control;
          clicking a pill selects + filters the map and its specific settings
          appear here. */}
      {selected && (
        <div className="mt-2" data-testid="explorer-filter">
          <button type="button" onClick={() => setFilterOpen((o) => !o)}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#F3F2EE] text-[#121212] hover:bg-[#ECEAE4] transition-colors"
            data-testid="explorer-filter-toggle" aria-expanded={filterOpen}>
            <SlidersHorizontal className="w-4 h-4" />
            {tr('Filtrs', 'Filter', 'Фильтр')}
            {activeFilterCount > 0 && (
              <span className="text-xs font-bold text-white bg-[#121212] rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1" data-testid="explorer-filter-count">{activeFilterCount}</span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
          </button>

          {filterOpen && (
            <div className="mt-2 bg-white rounded-2xl border border-[#E5E4E0] p-4 space-y-3" data-testid="explorer-filter-panel">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#121212]">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col(selected) }} />
                {React.createElement(META[selected].Icon, { className: 'w-4 h-4' })}
                {tr(META[selected].lv, META[selected].en, META[selected].ru)}
              </div>
              <label className="flex items-center gap-2 text-xs text-[#5C5C5C] cursor-pointer select-none">
                <input type="checkbox" checked={visibleOnly} onChange={(e) => setVisibleOnly(e.target.checked)} className="accent-[#2563EB]" data-testid="filter-visible" />
                {tr('Tikai redzamajā kartes daļā', 'Only in visible map area', 'Только в видимой области')}
              </label>
              {renderSpecificFilters(selected)}
            </div>
          )}
        </div>
      )}

      {/* Activity feed ("logs") — everything active, newest first, with a
          Tuvākie/Visa Latvija sort and a Visi/Ar vietu/Tiešsaistē toggle.
          Always visible; filtered by the chips. This replaces the old
          duplicate category results panel. */}
      <div ref={feedRef}>
        <ActivityFeed apiRef={apiRef} mapWrapRef={mapWrapRef} selected={selected} col={col} />
      </div>
    </section>
  );
});

export default MapCategoryExplorer;
